import { ReactNode, useMemo } from "react"
import { UseFormReturn, useWatch } from "react-hook-form"
import { WebhookCapabilityConfigSchema } from "schema"
import z from "zod"
import { useCapabilityVariables } from "../../hooks/useCapabilityVariables"
import { ActivationAccordion } from "./Shared/ActivationAccordion"
import { AgentAccordion } from "./Shared/AgentAccordion"
import { CampaignActivationAccordion } from "./Shared/CampaignActivationAccordion"
import { GeneralSection } from "./Shared/GeneralSection"
import { WebhookAccordion } from "./Shared/WebhookAccordion"

type Props = {
  form: UseFormReturn<z.infer<typeof WebhookCapabilityConfigSchema>>
  activationSettings: ReactNode
}

export function WebhookCapabilityForm({ form, activationSettings }: Props) {
  const dataToExtract = useWatch({ control: form.control, name: "dataToExtract" })
  const testResponseBody = useWatch({ control: form.control, name: "testResponseBody" })
  const mode = useWatch({ control: form.control, name: "mode" })
  const steps = useWatch({ control: form.control, name: "steps" })

  const stepTestResponses = useMemo(() => {
    if (mode !== "chain") return undefined
    return (steps ?? []).map((step) => step.testResponseBody)
  }, [mode, steps])

  // Variables available at each step:
  // - Activation: only customer (extraction hasn't happened yet)
  // - Webhook: customer + extracted data (dataToExtract fields become variables)
  // - Agent: customer + extracted + step responses + final response (last step in chain mode)
  const customerOnlyVariables = useCapabilityVariables([], null)
  const extractionVariables = useCapabilityVariables(dataToExtract, null)
  const allVariables = useCapabilityVariables(dataToExtract, testResponseBody, stepTestResponses)

  return (
    <>
      <GeneralSection form={form} />
      <ActivationAccordion form={form} variables={customerOnlyVariables} />
      <WebhookAccordion form={form} variables={extractionVariables} />
      <AgentAccordion form={form} showCache variables={allVariables} />
      <CampaignActivationAccordion activationSettings={activationSettings} />
    </>
  )
}
